import React, { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiMessageCircle, FiSearch } from 'react-icons/fi';
import { AuthContext } from '../../contexts/auth';
import { SidebarContext } from '../../AppRoutes';
import { supportChatApi as api } from '../../services/supportChatApi';
import ChatThread from '../../components/SupportChat/ChatThread';
import useChatPolling from '../../components/SupportChat/useChatPolling';
import LiveSupport, { OnlineUsers } from '../../components/SupportChat/LiveSupport';
import UsageMetrics from '../../components/SupportChat/UsageMetrics';
import '../../components/SupportChat/styles.css';

function Inbox() {
  const { t, i18n } = useTranslation('supportChat');
  const { sidebar } = useContext(SidebarContext);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  return <main className="eo-support-page" style={{ '--eo-sidebar': sidebar ? '250px' : '100px' }}>
    <div className="eo-support-title"><div><span>{t('inbox.eyebrow')}</span><h1>{t('inbox.heading')}</h1><p>{t('inbox.subtitle')}</p></div><FiMessageCircle /></div>
    <UsageMetrics />
    <div className={`eo-support-layout ${selected ? 'has-selection' : ''}`}>
      <aside className="eo-support-inbox" aria-label={t('inbox.listAria')}>
        <OnlineUsers onSelect={id => { setSelected(id); setRevision(value => value + 1); }} />
        <div className="eo-support-filters"><label><FiSearch /><input aria-label={t('inbox.searchAria')} placeholder={t('inbox.searchPlaceholder')} value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} /></label>
          <select aria-label={t('inbox.statusAria')} value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}><option value="open">{t('inbox.statusOpen')}</option><option value="closed">{t('inbox.statusClosed')}</option><option value="all">{t('inbox.statusAll')}</option></select></div>
        <InboxLoader key={`${search}:${status}:${page}:${revision}`} search={search} status={status} page={page}
          update={data => { setRows(data.conversations); setTotal(data.total); setError(''); setLoading(false); }}
          fail={message => { setError(message); setLoading(false); }} />
        {error && <div className="eo-chat-error" role="alert">{error} <button onClick={() => setRevision(value => value + 1)}>{t('inbox.retry')}</button></div>}
        <div className="eo-support-rows">
          {loading && <p className="eo-chat-empty">{t('inbox.loading')}</p>}
          {!loading && !error && !rows.length && <p className="eo-chat-empty">{t('inbox.empty')}</p>}
          {rows.map(row => <button key={row._id} type="button" className={`eo-support-row ${selected === row._id ? 'is-selected' : ''}`} onClick={() => setSelected(row._id)} aria-pressed={selected === row._id}>
            <div><strong>{row.restaurant}</strong><time>{new Date(row.lastMessageAt).toLocaleDateString(i18n.language)}</time></div>
            <span>{row.name} · {row.email}</span><p>{row.lastMessage}</p>
            <footer><small>{row.status === 'open' ? t('inbox.statusOpen') : t('inbox.statusClosed')}</small>{row.unread > 0 && <b>{t('inbox.unreadSuffix', { count: row.unread })}</b>}</footer>
          </button>)}
        </div>
        <nav className="eo-support-pagination" aria-label={t('inbox.paginationAria')}><button disabled={page === 1} onClick={() => setPage(value => value - 1)}>{t('inbox.previous')}</button><span>{page} / {Math.max(1, Math.ceil(total / 25))}</span><button disabled={page * 25 >= total} onClick={() => setPage(value => value + 1)}>{t('inbox.next')}</button></nav>
      </aside>
      <section className="eo-support-conversation" aria-label={t('inbox.conversationAria')}>
        {selected ? <><button type="button" className="eo-support-back" onClick={() => setSelected(null)}><FiArrowLeft /> {t('inbox.backToConversations')}</button>
          <LiveSupport key={`live:${selected}`} conversationId={selected} />
          <ChatThread key={selected} conversationId={selected} support onChanged={() => setRevision(value => value + 1)} /></>
          : <div className="eo-support-placeholder"><FiMessageCircle /><h2>{t('inbox.placeholderHeading')}</h2><p>{t('inbox.placeholderSubtitle')}</p></div>}
      </section>
    </div>
  </main>;
}

function InboxLoader({ search, status, page, update, fail }) {
  const { t } = useTranslation('supportChat');
  useChatPolling(async signal => {
    try {
      const { data } = await api.get('/api/v1/support-chat/conversations', { params: { search, status, page }, signal });
      if (!signal.aborted) update(data);
    } catch (err) { if (!signal.aborted) fail(t('inbox.loadError')); }
  }, 10000);
  return null;
}

export default function SupportInbox() {
  const { user } = useContext(AuthContext);
  return user?.role === 'Admin' ? <Inbox /> : <Navigate to="/" replace />;
}
